/**
 * A day's working windows as fields — start, end and a remove button per window — with the day's
 * one refusal under them (T-B2e). Worn by the week card's day editor and by the exception dialog
 * (T-B2w), which edit the same shape by the same two rules (`WindowGridValidator`).
 *
 * The windows that fight are edged in the warning ink and the sentence says what to do about them;
 * it is a note beside the fields, never a colour alone. The times are native `time` fields on the
 * trainer's clock, so the keyboard and the platform's own picker both work. The fighting fields
 * are described by that sentence. Removing a window moves focus to the window after it, or to the
 * scope's „Dodaj okno" when it was the last (`data-window-scope` marks the scope, `data-add-window`
 * its add button).
 */

import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { windowProblemText, windowSpan } from '../model/scheduleWords';
import { minutesOfTime, paddedClock } from '../model/trainerClock';
import type { PatternWindow, WindowProblem } from '../model/weekPattern';
import styles from './ScheduleTab.module.css';

interface WindowFieldsProps {
  readonly windows: readonly PatternWindow[];
  readonly bad: readonly string[];
  readonly problem: WindowProblem | null;
  /** Fills the row's width (the dialog) rather than the canon's fixed 96px fields (the card). */
  readonly wide?: boolean;
  readonly onRetime: (
    id: string,
    change: { readonly start?: number; readonly end?: number },
  ) => void;
  readonly onRemove: (id: string) => void;
}

export function WindowFields({
  windows,
  bad,
  problem,
  wide = false,
  onRetime,
  onRemove,
}: WindowFieldsProps) {
  const { t } = useTranslation();
  const warnId = useId();
  const removed = useRef<{ readonly scope: Element; readonly index: number } | null>(null);
  useEffect(() => {
    const at = removed.current;
    if (at === null) return;
    removed.current = null;
    const rows = at.scope.querySelectorAll<HTMLElement>('[data-window-row]');
    const target =
      rows[at.index]?.querySelector<HTMLElement>('input') ??
      at.scope.querySelector<HTMLElement>('[data-add-window]') ??
      rows[rows.length - 1]?.querySelector<HTMLElement>('input');
    target?.focus();
  });
  const field = (window: PatternWindow, edge: 'start' | 'end', index: number) => (
    <input
      type="time"
      step={60}
      className={`${String(styles.time)} ${wide ? String(styles.timeWide) : ''} ${bad.includes(window.id) ? String(styles.timeBad) : ''}`}
      aria-label={t(
        edge === 'start' ? 'web:booking.schedule.windowStart' : 'web:booking.schedule.windowEnd',
        {
          n: index + 1,
        },
      )}
      aria-invalid={bad.includes(window.id) ? true : undefined}
      aria-describedby={bad.includes(window.id) && problem !== null ? warnId : undefined}
      value={paddedClock(window[edge])}
      onChange={(event) => {
        const minutes = minutesOfTime(event.target.value);
        if (minutes !== null) onRetime(window.id, { [edge]: minutes });
      }}
    />
  );
  return (
    <>
      {windows.map((window, index) => (
        <div key={window.id} className={styles.windowRow} data-window-row>
          {field(window, 'start', index)}
          <span className={styles.dash} aria-hidden="true">
            –
          </span>
          {field(window, 'end', index)}
          <button
            type="button"
            className={styles.squareButton}
            aria-label={t('web:booking.schedule.removeWindow', { window: windowSpan(window) })}
            onClick={(event) => {
              const scope = event.currentTarget.closest('[data-window-scope]');
              if (scope !== null) removed.current = { scope, index };
              onRemove(window.id);
            }}
          >
            <CloseGlyph className={styles.squareGlyph} />
          </button>
        </div>
      ))}
      {problem === null ? null : (
        <p id={warnId} className={styles.warnBox} role="alert">
          <WarningGlyph className={styles.warnGlyph} />
          <span>{windowProblemText(problem, t)}</span>
        </p>
      )}
    </>
  );
}
